import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { HelmetProvider } from "@/lib/helmet-compat";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";
import { HOME_TITLE, HOME_DESCRIPTION } from "@/lib/seo";
import { GDPRCookieConsent } from "@/components/CookieConsent";
import { AnalyticsLoader } from "@/components/AnalyticsLoader";
import { LocalBusinessSchema } from "@/components/StructuredData";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ matches }) => {
    const isNotFound = matches.some(
      (m) => m.status === "notFound" || (m as { globalNotFound?: boolean }).globalNotFound,
    );
    return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      ...(isNotFound
        ? [
            { title: "Seite nicht gefunden | Clicks DMP" },
            { name: "robots", content: "noindex" },
          ]
        : [{ title: HOME_TITLE }, { name: "description", content: HOME_DESCRIPTION }]),
      { name: "author", content: "Clicks Design & Media Production" },
      { name: "geo.region", content: "DE-BW" },
      { name: "geo.placename", content: "Mannheim" },
      { name: "geo.position", content: "49.46316;8.48497" },
      { name: "ICBM", content: "49.46316, 8.48497" },
      {
        name: "google-site-verification",
        content: "MfQyMEWJtgQcXHa-L9FJbS9TgaR5-thaSldH3PlkkUY",
      },
      {
        httpEquiv: "Content-Security-Policy",
        content:
          "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com; style-src 'self' 'unsafe-inline'; img-src 'self' data: https: blob:; font-src 'self' data:; connect-src 'self' https://*.supabase.co https://*.google-analytics.com https://*.analytics.google.com https://*.googletagmanager.com; frame-src 'self' https://www.googletagmanager.com; child-src 'self' https://www.googletagmanager.com; media-src 'self' blob: https:; object-src 'none'; base-uri 'self'; form-action 'self'; upgrade-insecure-requests",
      },
      { property: "og:site_name", content: "Clicks DMP" },
      { property: "og:locale", content: "de_DE" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:site", content: "@clicksdmp" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "dns-prefetch", href: "https://buhajnrjzecedelnxllv.supabase.co" },
      {
        rel: "preconnect",
        href: "https://buhajnrjzecedelnxllv.supabase.co",
        crossOrigin: "anonymous",
      },
    ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => <NotFound />,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="de" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ScrollToTop />
          <LocalBusinessSchema />
          <AuthProvider>
            <Suspense
              fallback={
                <div className="min-h-screen flex items-center justify-center">Loading...</div>
              }
            >
              <Outlet />
            </Suspense>
          </AuthProvider>
          <GDPRCookieConsent />
          <AnalyticsLoader />
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error, reset]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
      <div className="max-w-md w-full text-center space-y-6 p-8">
        <h1 className="text-2xl font-bold">Diese Seite konnte nicht geladen werden</h1>
        <p className="text-muted-foreground">
          Bei uns ist etwas schiefgelaufen. Bitte versuchen Sie es erneut oder kehren Sie zur
          Startseite zurück.
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <button
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Erneut versuchen
          </button>
          <a href="/" className="border border-border px-4 py-2 rounded-md">
            Zur Startseite
          </a>
        </div>
      </div>
    </div>
  );
}
